import throttle from 'lodash/throttle'
import { useCallback, useEffect, useMemo, useRef } from 'react'

/**
 * Returns a poke handler for `usePusherSubscribe` that coalesces poke bursts into at most one
 * refetch per throttle window (leading + trailing).
 *
 * Backend jobs poke channels on every completion, which can arrive in bursts (e.g. one poke per
 * pricing batch job), and each refetch of a large query is expensive to process on the main
 * thread. Hidden tabs skip poke-driven refetches entirely: processing a large response in a
 * background tab blocks the renderer's main thread, which same-site tabs often share, so a hidden
 * tab's work can freeze the tab the user is actually looking at. If a poke arrives while hidden,
 * a single refetch fires when the tab becomes visible again.
 */
export function useThrottledPokeRefetch(
  refetch: () => Promise<unknown>,
  throttleMs = 10_000
): () => void {
  const throttledRefetch = useMemo(
    () =>
      throttle(
        () => {
          refetch().catch(e => console.error(e))
        },
        throttleMs,
        { leading: true, trailing: true }
      ),
    [refetch, throttleMs]
  )
  useEffect(() => () => throttledRefetch.cancel(), [throttledRefetch])

  const wasPokedWhileHiddenRef = useRef(false)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && wasPokedWhileHiddenRef.current) {
        wasPokedWhileHiddenRef.current = false
        throttledRefetch()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [throttledRefetch])

  return useCallback(() => {
    if (document.visibilityState === 'hidden') {
      wasPokedWhileHiddenRef.current = true
      return
    }
    throttledRefetch()
  }, [throttledRefetch])
}
